import { EmptyStateNoResults, InlineEmptyStateOnboarding, List, ListItemContent, ListItemLink, Pagination, type PrimaryPageAction, SearchInput } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { LibraryVariableSetResource, Repository, ResourceCollection } from "@octopusdeploy/octopus-server-client";
import { Permission, VariableSetContentType } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCallback } from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import MarkdownDescription from "~/components/MarkdownDescription";
import { formatUrl } from "~/components/Navigation/ExternalLink/ExternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import ListTitle from "~/primitiveComponents/dataDisplay/ListTitle/ListTitle";
export interface ScriptModulesQueryParams {
    scriptModuleName: string;
    page: number | undefined;
    pageSize: number | undefined;
}
export function scriptModulesPageLoader(repository: Repository, queryParams: ScriptModulesQueryParams): Promise<ScriptModulesPageLoaderData> {
    return getPaginatedScriptModules(repository, queryParams);
}
export const scriptModulesPageTitle = "Script Modules";
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export interface ScriptModulesPageLoaderData {
    scriptModules: ResourceCollection<LibraryVariableSetResource>;
    hasScriptModules: boolean;
}
interface ScriptModulesPageProps {
    spaceId: string;
    loaderData: ScriptModulesPageLoaderData;
    queryParams: ScriptModulesQueryParams;
    setQueryParams: QueryParamValuesSetter<ScriptModulesQueryParams>;
}
export function ScriptModulesPage({ spaceId, loaderData, queryParams, setQueryParams }: ScriptModulesPageProps) {
    const { result: scriptModulesResult } = usePaginatedScriptModulesQuery(loaderData, queryParams);
    const { scriptModules, hasScriptModules } = scriptModulesResult;
    const [debouncedScriptModuleName, setDebouncedScriptModuleName] = useDebouncedExternalState(queryParams.scriptModuleName, (value) => setQueryParams({ scriptModuleName: value, page: 1 }), 200);
    const addScriptModulePageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: isAllowed({ permission: Permission.LibraryVariableSetCreate }),
        label: "Add Script Module",
        path: links.createScriptModulePage.generateUrl({ spaceId }),
    };
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    if (!hasScriptModules) {
        return (<PageContent header={{ title: scriptModulesPageTitle, primaryAction: addScriptModulePageAction }}>
                <BorderedContent>
                    <InlineEmptyStateOnboarding entityName="Script Modules" primaryAction={addScriptModulePageAction} learnMore={{ label: "Docs: Script Modules", path: formatUrl("ScriptModules") }}/>
                </BorderedContent>
            </PageContent>);
    }
    const hideBottomPagination = scriptModules.Items.length < 20;
    return (<PageContent header={{ title: scriptModulesPageTitle, primaryAction: addScriptModulePageAction }} pagination={{
            ui: (<Pagination label={"Script Modules"} totalResults={scriptModules.TotalResults} itemsPerPageOptions={pageSizeOptions} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} selectedItemsPerPage={scriptModules.ItemsPerPage}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} filters={{ inputs: [<SearchInput autoFocus value={debouncedScriptModuleName} onChange={setDebouncedScriptModuleName} accessibleName={"Search script module by name"} placeholder="Search by name"/>] }}>
            <BorderedContent>
                <List<LibraryVariableSetResource> items={scriptModules.Items} renderRow={({ item: scriptModule }) => buildRow(scriptModule)} empty={<EmptyStateNoResults />}/>
            </BorderedContent>
        </PageContent>);
    function buildRow(scriptModule: LibraryVariableSetResource) {
        return (<ListItemLink href={links.editScriptModulePage.generateUrl({ spaceId, scriptModuleId: scriptModule.Id })} key={scriptModule.Id}>
                <ListItemContent>
                    <ListTitle>{scriptModule.Name}</ListTitle>
                    <MarkdownDescription markup={scriptModule.Description}/>
                </ListItemContent>
            </ListItemLink>);
    }
}
interface GetPaginatedScriptModulesOptions {
    page: number | undefined;
    pageSize: number | undefined;
    scriptModuleName: string | undefined;
}
async function getPaginatedScriptModules(repository: Repository, { page = 1, pageSize = defaultPageSize, scriptModuleName = "" }: GetPaginatedScriptModulesOptions) {
    const scriptModules = repository.LibraryVariableSets.list({
        contentType: VariableSetContentType.ScriptModule,
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialName: scriptModuleName,
    });
    const scriptModuleStats = repository.LibraryVariableSets.list({
        skip: 0,
        take: 0,
        contentType: VariableSetContentType.ScriptModule,
    });
    return {
        scriptModules: await scriptModules,
        hasScriptModules: (await scriptModuleStats).TotalResults > 0,
    };
}
function usePaginatedScriptModulesQuery(initialResult: ScriptModulesPageLoaderData, queryParams: ScriptModulesQueryParams) {
    const { page, pageSize, scriptModuleName } = queryParams;
    return useQuery((repository) => getPaginatedScriptModules(repository, { page, pageSize, scriptModuleName }), [page, pageSize, scriptModuleName], "Get paginated script modules", { initialResult });
}
